import React, { useState } from 'react';
import { Box, Tooltip, Typography, Zoom } from '@mui/material';

/**
 * ThumbnailZoom
 * Al pasar el mouse por encima de la miniatura del item, se agranda con efecto animado
 * de zoom in en un pequeño modal flotante con estética gamer neón y desaparece
 * inmediatamente al perder el foco del mouse.
 */
export default function ThumbnailZoom({
  src,
  highResSrc,
  alt = 'Miniatura',
  title = '',
  badge = null,
  width = 44,
  height = 28,
  zoomWidth = 250,
  placement = 'right',
  onClick,
}) {
  const [imgError, setImgError] = useState(false);

  // Obtener versión de mayor calidad si la URL es de YouTube default.jpg
  const resolvedHighRes = highResSrc || (
    typeof src === 'string' && src.includes('/default.jpg')
      ? src.replace('/default.jpg', '/hqdefault.jpg')
      : src
  );

  const previewSrc = imgError ? src : (resolvedHighRes || src);

  return (
    <Tooltip
      placement={placement}
      arrow={false}
      enterDelay={120}
      leaveDelay={40}
      disableInteractive
      slots={{
        transition: Zoom,
      }}
      slotProps={{
        transition: { timeout: 200 },
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 10],
              },
            },
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                padding: 8,
              },
            },
          ],
        },
        tooltip: {
          sx: {
            p: 0,
            background: 'transparent',
            boxShadow: 'none',
            maxWidth: 'none',
            overflow: 'visible',
          },
        },
      }}
      title={
        <Box
          sx={{
            width: zoomWidth,
            borderRadius: '12px',
            overflow: 'hidden',
            background: 'rgba(18, 8, 32, 0.96)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 45, 155, 0.5)',
            boxShadow: '0 16px 42px rgba(0, 0, 0, 0.88), 0 0 22px rgba(255, 45, 155, 0.35)',
            p: '5px',
            pointerEvents: 'none',
            animation: 'modalThumbnailZoomIn 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
            '@keyframes modalThumbnailZoomIn': {
              '0%': {
                opacity: 0,
                transform: 'scale(0.8)',
              },
              '100%': {
                opacity: 1,
                transform: 'scale(1)',
              },
            },
          }}
        >
          {/* Contenedor 16:9 de la miniatura ampliada */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '16/9',
              borderRadius: '8px',
              overflow: 'hidden',
              background: '#05020a',
            }}
          >
            <img
              src={previewSrc}
              alt={alt}
              onError={() => setImgError(true)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
            {badge && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 6,
                  left: 6,
                  px: '6px',
                  py: '2px',
                  fontSize: '0.68rem',
                  fontWeight: 800,
                  background: 'rgba(0, 0, 0, 0.82)',
                  color: '#ff2d9b',
                  border: '1px solid rgba(255, 45, 155, 0.45)',
                  borderRadius: '4px',
                  lineHeight: 1.1,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
                }}
              >
                {badge}
              </Box>
            )}
          </Box>
          {title && (
            <Typography
              sx={{
                px: 1,
                pt: 0.8,
                pb: 0.4,
                fontSize: '0.74rem',
                fontWeight: 600,
                color: '#ffffff',
                lineHeight: 1.25,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {title}
            </Typography>
          )}
        </Box>
      }
    >
      {/* Miniatura en línea del item */}
      <Box
        onClick={onClick}
        sx={{
          position: 'relative',
          width,
          height,
          flexShrink: 0,
          borderRadius: '5px',
          overflow: 'hidden',
          background: '#000',
          cursor: 'zoom-in',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          transition: 'all 0.18s ease',
          '&:hover': {
            transform: 'scale(1.08)',
            borderColor: '#ff2d9b',
            boxShadow: '0 0 10px rgba(255, 45, 155, 0.55)',
          },
        }}
      >
        <img
          alt={alt}
          src={src}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        {badge && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 1,
              left: 2,
              px: '3px',
              fontSize: '0.6rem',
              fontWeight: 800,
              background: 'rgba(0, 0, 0, 0.75)',
              color: '#fff',
              borderRadius: '3px',
              lineHeight: 1.1,
            }}
          >
            {badge}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}
